import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import { journalAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, STALE } from '../hooks/useQuery';
import { setQueryData, makeKey } from '../lib/queryCache';
import { ListRowsSkeleton, ErrorState } from '../components/ui/Skeleton';
import XPToast from '../components/ui/XPToast';
import GrammarErrorPopup from '../components/ui/GrammarErrorPopup';
import SpeakButton from '../components/ui/SpeakButton';
import PageHeader from '../components/ui/PageHeader';
import {
  PenLine,
  Clock3,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Send,
} from 'lucide-react';

const WritingJournalPage = () => {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);
  const [xpToast, setXpToast] = useState(null);
  const [selectedError, setSelectedError] = useState(null);

  const {
    data: fetchedEntries,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery(
    'journal_entries',
    null,
    async () => {
      const res = await journalAPI.list();
      return res.data.entries || [];
    },
    { staleTime: STALE.medium }
  );

  const [localEntries, setLocalEntries] = useState(null);

  useEffect(() => {
    if (fetchedEntries) {
      setLocalEntries(fetchedEntries);
    }
  }, [fetchedEntries]);

  const entries = localEntries || fetchedEntries || [];

  const handleSubmit = async () => {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    setResult(null);
    setSubmitError(null);
    try {
      const res = await journalAPI.submit(text);
      const data = res.data;
      setResult(data);
      if (data.xp_awarded > 0) {
        setXpToast({ amount: data.xp_awarded, reason: 'journal_entry' });
      }
      setText('');
      const updated = [data, ...entries];
      setLocalEntries(updated);
      if (user?.id) {
        const key = makeKey(user.id, 'journal_entries', null);
        setQueryData(key, updated);
      }
    } catch (e) {
      console.error(e);
      setSubmitError('Dështoi kontrolli i fjalisë.');
    } finally {
      setSubmitting(false);
    }
  };

  const scoreColor = (score) => {
    if (score >= 90) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    if (score >= 70) return 'text-amber-800 bg-amber-50 border-amber-200';
    return 'text-red-700 bg-red-50 border-red-200';
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-1 pb-10 font-sans sm:px-3">
        {/* Unified Header */}
        <PageHeader
          icon={PenLine}
          badge="Ushtrim Shkrimi"
          title="Ditari im në Gjermanisht"
          description="Shkruaj mendimet, fjalitë ose paragrafët e ditës në gjermanisht dhe merr korrigjim të menjëhershëm të gabimeve gramatikore."
        />

        {submitError && <ErrorState message={submitError} />}

        {/* Input Area Card */}
        <div className="overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-sm">
          <div aria-hidden="true" className="flex h-1 shrink-0">
            <span className="flex-1 bg-zinc-950" />
            <span className="flex-1 bg-[#DD0000]" />
            <span className="flex-1 bg-[#FFCE00]" />
          </div>

          <textarea
            id="journal-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Shkruaj fjali në gjermanisht këtu... p.sh. Heute habe ich fleißig Deutsch gelernt und neue Wörter wiederholt."
            rows={4}
            className="w-full p-5 text-sm text-zinc-900 placeholder:text-zinc-400 resize-none focus:outline-none leading-relaxed"
          />

          <div className="flex items-center justify-between px-5 py-3 border-t border-zinc-100 bg-[#fafafa]">
            <span className="text-xs text-zinc-400 font-medium">{text.length} karaktere</span>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!text.trim() || submitting}
              className="inline-flex items-center gap-2 rounded-xl bg-zinc-950 px-5 py-2.5 text-xs font-bold text-white hover:bg-[#DD0000] disabled:opacity-40 transition"
            >
              <Send size={13} />
              {submitting ? 'Duke kontrolluar...' : 'Kontrollo Gramatikën'}
            </button>
          </div>
        </div>

        {/* Last Check Result */}
        {result && (
          <div className="overflow-hidden rounded-2xl border border-zinc-200/80 bg-white p-5 sm:p-7 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <h3 className="font-extrabold text-sm text-zinc-900 uppercase tracking-wider">Rezultati i Kontrollit</h3>
              <div className="flex items-center gap-2">
                <SpeakButton text={result.text} lang="de-DE" />
                <span className={`px-3 py-1 rounded-full text-xs font-black border ${scoreColor(result.score)}`}>
                  {result.score}% Saktësi
                </span>
              </div>
            </div>

            <p className="text-sm font-semibold text-zinc-800 leading-relaxed bg-zinc-50 p-4 rounded-xl border border-zinc-100">
              "{result.text}"
            </p>

            {result.errors && result.errors.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-extrabold uppercase tracking-wider text-zinc-400">
                  Gabimet e Identifikuara ({result.errors.length})
                </p>
                {result.errors.map((err, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedError(err)}
                    className="w-full text-left flex items-start gap-3 p-3.5 rounded-xl bg-red-50/60 border border-red-200 hover:bg-red-100/60 transition"
                  >
                    <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-red-800">{err.error_type}</p>
                      <p className="text-xs text-red-700 mt-0.5">{err.explanation_sq}</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2.5 text-emerald-700 bg-emerald-50 border border-emerald-200 p-3.5 rounded-xl">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                <span className="text-xs font-bold">Asnjë gabim gramatikor! Shkrim i shkëlqyer!</span>
              </div>
            )}

            {result.xp_awarded > 0 && (
              <div className="inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1 text-xs font-extrabold text-amber-800">
                <Sparkles size={13} className="text-amber-600" /> +{result.xp_awarded} XP të fituara
              </div>
            )}
          </div>
        )}

        {/* Past entries history */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-extrabold uppercase tracking-wider text-zinc-400">
              Hyrjet e Mëparshme
            </h2>
            <span className="text-xs text-zinc-400">{entries.length} regjistrime</span>
          </div>

          {queryError && !entries.length && (
            <ErrorState message="Nuk mund të ngarkoheshin hyrjet e mëparshme." onRetry={refetch} />
          )}

          {isLoading && <ListRowsSkeleton rows={3} height="h-20" />}

          {!isLoading && entries.length === 0 && (
            <div className="rounded-2xl border border-dashed border-zinc-200 bg-white p-12 text-center text-xs text-zinc-400">
              Nuk ka hyrje akoma. Shkruaj fjalinë tënde të parë më sipër!
            </div>
          )}

          {!isLoading &&
            entries.map((e) => (
              <div
                key={e.id}
                className="group rounded-xl border border-zinc-200/80 bg-white p-4 shadow-2xs hover:border-zinc-300 transition space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs text-zinc-400 font-medium">
                    <Clock3 className="w-3.5 h-3.5" />
                    {new Date(e.created_at).toLocaleDateString('sq-AL', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <SpeakButton text={e.text} lang="de-DE" />
                    {e.score != null && (
                      <span className={`text-[11px] px-2 py-0.5 rounded-md font-bold border ${scoreColor(e.score)}`}>
                        {e.score}%
                      </span>
                    )}
                    {e.xp_awarded > 0 && (
                      <span className="text-[11px] font-extrabold text-amber-800 bg-amber-50 px-2 py-0.5 rounded-md">
                        +{e.xp_awarded} XP
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-xs sm:text-sm font-semibold text-zinc-800 leading-relaxed">{e.text}</p>
                {e.errors?.length > 0 && (
                  <p className="text-xs text-red-600 font-medium">
                    {e.errors.length} gabim{e.errors.length > 1 ? 'e' : ''} i gjetur
                  </p>
                )}
              </div>
            ))}
        </div>
      </div>

      {selectedError && (
        <GrammarErrorPopup error={selectedError} onClose={() => setSelectedError(null)} />
      )}

      {xpToast && (
        <XPToast amount={xpToast.amount} reason={xpToast.reason} onDone={() => setXpToast(null)} />
      )}
    </DashboardLayout>
  );
};

export default WritingJournalPage;
